import type { Notification } from '../types';

/**
 * The bell groups the notices of one type for one tournament into ONE row
 * (#495, operator 2026-09-26): "21 boxers confirmed: Jaunimo varžybos". On the
 * tournament day of 2026-09-26 one coach had 69 confirmation notices.
 *
 * Only the organization types group. A time-critical notice (`bout_upcoming`,
 * `bout_live`) and a summary always stay a row of their own, so the bell never
 * hides "Bout #12 is up next!" inside a group. Two or more notices form a
 * group; one notice stays a row. A group stands where its newest notice
 * stands (the list comes newest first).
 */
export const GROUPED_TYPES: ReadonlySet<string> = new Set([
  'registration_confirmed', 'bout_assigned', 'bout_changed',
  // #535 — a change to a registration. #575: also from inside the team; it goes to the coach who registered the athlete and to the account of the athlete, never to the actor.
  'registration_created', 'registration_unconfirmed', 'registration_removed', 'registration_category_changed',
]);

export type BellEntry =
  | { kind: 'one'; notice: Notification }
  | { kind: 'group'; key: string; type: string; tournamentName: string; notices: Notification[] };

export function groupNotifications(rows: Notification[]): BellEntry[] {
  const keyOf = (n: Notification) => (GROUPED_TYPES.has(n.type) && n.tournament_id ? `${n.type}|${n.tournament_id}` : null);
  const size = new Map<string, number>();
  for (const n of rows) {
    const key = keyOf(n);
    if (key) size.set(key, (size.get(key) ?? 0) + 1);
  }
  const out: BellEntry[] = [];
  const open = new Map<string, Extract<BellEntry, { kind: 'group' }>>();
  for (const n of rows) {
    const key = keyOf(n);
    if (!key || (size.get(key) ?? 0) < 2) { out.push({ kind: 'one', notice: n }); continue; }
    let group = open.get(key);
    if (!group) {
      group = { kind: 'group', key, type: n.type, tournamentName: '', notices: [] };
      open.set(key, group);
      out.push(group);
    }
    // A live notice (`notification:new`) is the bare row of the INSERT, with
    // no `tournament_name`: the name comes from another notice of the group,
    // or from the params that the server wrote (#535).
    if (!group.tournamentName) group.tournamentName = n.tournament_name || String(n.params?.tournament ?? '');
    group.notices.push(n);
  }
  return out;
}
